import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

const STAGES = [
  "Video ingestion & frame normalization",
  "Stance & V-grip geometry detection",
  "Trigger & weight-transfer analysis",
  "Shot execution & bat-path tracking",
  "AI coach report synthesis",
];

export const AnalysisProgress = ({ uploadPct }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    // once upload completes, advance through analysis stages on a timer
    if (uploadPct < 100) {
      setActive(0);
      return;
    }
    setActive(1);
    const timers = STAGES.map((_, i) =>
      i >= 1 ? setTimeout(() => setActive((a) => Math.max(a, i)), (i - 1) * 4500 + 2000) : null
    );
    return () => timers.forEach((t) => t && clearTimeout(t));
  }, [uploadPct]);

  return (
    <div data-testid="analysis-progress" className="rounded-2xl border border-slate-800 bg-[#12181F] p-6 animate-fade-up">
      <div className="flex items-center gap-3 mb-6">
        <div className="relative h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
          <Loader2 className="h-5 w-5 text-emerald-400 animate-spin" />
        </div>
        <div>
          <p className="font-heading font-bold text-slate-100">Analyzing technique</p>
          <p className="font-mono-tel text-[11px] text-slate-500 tracking-wide">
            {uploadPct < 100 ? `Uploading clip · ${uploadPct}%` : "AI vision processing…"}
          </p>
        </div>
      </div>

      <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden mb-6">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all duration-500"
          style={{ width: uploadPct < 100 ? `${uploadPct}%` : `${20 + active * 16}%` }}
        />
      </div>

      <div className="flex flex-col gap-3">
        {STAGES.map((s, i) => {
          const done = i < active || (uploadPct >= 100 && i === 0);
          const current = i === active && uploadPct >= 100;
          return (
            <div key={s} className="flex items-center gap-3">
              <div
                className={`h-6 w-6 rounded-full flex items-center justify-center border shrink-0 transition-colors ${
                  done
                    ? "bg-emerald-500/20 border-emerald-500/50"
                    : current
                    ? "bg-cyan-500/15 border-cyan-500/50"
                    : "bg-slate-800/50 border-slate-700"
                }`}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400" strokeWidth={3} />
                ) : current ? (
                  <Loader2 className="h-3.5 w-3.5 text-cyan-400 animate-spin" />
                ) : (
                  <span className="font-mono-tel text-[10px] text-slate-500">{i + 1}</span>
                )}
              </div>
              <span
                className={`text-sm transition-colors ${
                  done ? "text-slate-300" : current ? "text-cyan-300 font-semibold" : "text-slate-500"
                }`}
              >
                {s}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
